// Transport: the song through the Signalsmith Stretch AudioWorklet, so speed changes keep pitch and a loop
// repeats without a gap. It owns the AudioContext and the `Clock` (clock.ts): every play, pause, seek,
// speed change and loop change makes a new clock at the context time it happens and schedules the same change on
// the node, so what the engine computes and what the node does are the same map (checked against the node's own
// position reports in e2e/engine.spec.ts).
//
// Position, speed and loop: `node.schedule({ output, input, rate, loopStart, loopEnd })`. `output` is the context
// time at which `input` is at the node's output; the node compensates for its own latency (measured: about 1.3 ms
// left, STRETCH_RESIDUAL_S). The speaker's lag is added on the reading side (`heard`).
//
// The source is given to the node by moving its sample arrays (no copy kept here, so a five-minute stereo file is
// held once). For a looping source (the Rhythm Lab) song time wraps at `duration` and the node loops the middle of
// three copies (lab.ts), which `inputOffset` accounts for.

import type { StretchNode } from 'signalsmith-stretch';
import { heardSong, songAt, STRETCH_RESIDUAL_S, trimHistory, type Clock, type Loop } from './clock.js';
import { chooseLatency, type Latency } from './latency.js';

export interface Source {
  /** One Float32Array per channel at the context's sample rate. Ownership passes to the transport. */
  readonly channels: Float32Array[];
  readonly duration: number;
  /** true: song time is one lap that wraps at `duration` (the lab). false: a file that ends. */
  readonly wrap: boolean;
  /** Seconds added to song time to find the sound in `channels`. */
  readonly inputOffset: number;
}

export class Transport {
  readonly ctx = new AudioContext();
  /** The clock, replaced at every change. */
  clock: Clock = { ctx: 0, song: 0, rate: 1, loop: null, playing: false, latency: STRETCH_RESIDUAL_S };
  /** Last position the node reported, with the context time it arrived: an independent reading of the node, for tests and diagnostics. */
  probe: { input: number; ctx: number } | null = null;
  /** Called after every clock change. */
  onChange: () => void = () => {};
  /** Called when a file plays to its end. */
  onEnded: () => void = () => {};

  private readonly music = this.ctx.createGain();
  private node: Promise<StretchNode> | null = null;
  private source: Source | null = null;
  /** Counts the sources loaded or let go: a `replace` still waiting when it changes is for a source that is gone. */
  private epoch = 0;
  private loaded: Promise<void> = Promise.resolve();
  private userLoop: Loop | null = null;
  private rate = 1;
  private watch = 0;
  private calibrationS: number | null = null;

  constructor() {
    this.music.connect(this.ctx.destination);
    // The system took the audio away (a call, Siri, another app): the context stops, so the song is not playing. Show it as paused.
    this.ctx.addEventListener('statechange', () => {
      if (this.ctx.state !== 'running' && this.clock.playing) this.pause();
    });
  }

  get duration(): number {
    return this.source?.duration ?? 0;
  }

  get sampleRate(): number {
    return this.ctx.sampleRate;
  }

  get playing(): boolean {
    return this.clock.playing;
  }

  /** The delay to the listener, how it was found (latency.ts), and the figure a calibration run measures against. */
  get latency(): Latency {
    const ctx = this.ctx as AudioContext & { outputLatency?: number };
    return chooseLatency(ctx.outputLatency, ctx.baseLatency, this.calibrationS);
  }

  /** Seconds between the context clock and the speaker (applied once, in `heard`). */
  get outputLatency(): number {
    return this.latency.used;
  }

  /** The device's calibration, used as part of the delay only where the browser does not report its output latency. */
  setCalibration(offsetS: number | null): void {
    this.calibrationS = offsetS;
  }

  /** Make a node, once. The module and the node are set up without waiting for a user gesture; playing waits for the context. */
  private ensureNode(): Promise<StretchNode> {
    // The library (about 110 kB with its worklet) is a chunk of its own: Home does not load it.
    this.node ??= import('signalsmith-stretch').then((m) => m.default(this.ctx)).then(async (node) => {
      node.connect(this.music);
      await node.setUpdateInterval(0.1, (input) => {
        this.probe = { input: input - (this.source?.inputOffset ?? 0), ctx: this.ctx.currentTime };
      });
      return node;
    }).catch((e: unknown) => {
      this.node = null; // a failed load is not kept: the next load tries again
      throw e;
    });
    return this.node;
  }

  /** Let go of the song: its samples leave the worklet (a new song's decoding then starts without the old one in memory). */
  release(): void {
    this.pause();
    this.epoch++;
    this.source = null;
    this.userLoop = null;
    this.loaded = (this.node ?? Promise.resolve(null)).then((node) => node?.dropBuffers()).then(() => undefined, () => undefined);
  }

  /** Try the last source again (the player's module or worklet failed to load). */
  retry(): Promise<void> {
    const source = this.source;
    if (!source || !source.channels[0]?.byteLength) return Promise.reject(new Error('the song is no longer held'));
    return this.load(source);
  }

  /** Replace the source. Stops playing and goes to the start. */
  load(source: Source): Promise<void> {
    this.pause();
    this.epoch++;
    this.source = source;
    this.userLoop = null;
    this.clock = { ...this.clock, ctx: this.ctx.currentTime, song: 0, loop: this.effectiveLoop(), playing: false, previous: undefined };
    const mine = source;
    this.loaded = this.ensureNode().then(async (node) => {
      await node.dropBuffers();
      // Moved, not copied, to the worklet.
      await node.addBuffers(mine.channels, mine.channels.map((c) => c.buffer));
      await node.schedule({ active: false, output: this.ctx.currentTime });
    });
    this.onChange();
    return this.loaded;
  }

  /**
   * Swap the sound of a looping source for another of the same length and layout, without stopping, moving or touching the clock: the node's
   * buffers are replaced in place (dropped and added back to back) and its time map, which points at the same song position in the same layout,
   * stays as it is. Playing or paused, the position, speed and practice loop are unchanged; the node renders silence only for the instant between
   * the two messages. (The new samples must not be added before the old are dropped: the stretch library cannot read a block that spans two buffers.)
   * A source of another length or layout is loaded as a new source (`load`).
   */
  replace(source: Source): Promise<void> {
    const old = this.source;
    if (!old || !old.wrap || !source.wrap || Math.abs(old.duration - source.duration) > 1e-9 || Math.abs((old.inputOffset ?? 0) - (source.inputOffset ?? 0)) > 1e-9) return this.load(source);
    const epoch = this.epoch;
    this.loaded = this.loaded.then(async () => {
      const node = await this.ensureNode();
      if (epoch !== this.epoch) return; // a source was loaded or let go meanwhile: this one is not wanted
      this.source = source;
      await Promise.all([node.dropBuffers(), node.addBuffers(source.channels, source.channels.map((c) => c.buffer))]); // moved, not copied
    });
    return this.loaded;
  }

  /** The loop the clock follows: the practice loop, or the whole lap of a looping source. */
  private effectiveLoop(): Loop | null {
    if (this.userLoop) return this.userLoop;
    return this.source?.wrap ? { start: 0, end: this.source.duration } : null;
  }

  /** Song position at the node's output now, not wrapped past the end of a file. */
  private nodeSong(): number {
    const s = songAt(this.clock, this.ctx.currentTime);
    return this.source?.wrap ? s : Math.min(Math.max(s, 0), this.duration);
  }

  /** Song position the listener hears now. */
  heard(): number {
    const s = heardSong(this.clock, this.ctx.currentTime, this.outputLatency);
    return this.source?.wrap ? s : Math.min(Math.max(s, 0), this.duration);
  }

  /** Make a new clock at this moment and put the same map on the node. */
  private apply(song: number, playing: boolean): void {
    const now = this.ctx.currentTime;
    const loop = this.effectiveLoop();
    this.clock = { ctx: now, song, rate: this.rate, loop, playing, latency: STRETCH_RESIDUAL_S, previous: trimHistory(this.clock, now) };
    const offset = this.source?.inputOffset ?? 0;
    void this.node?.then((node) => node.schedule({
      active: playing,
      output: now,
      input: song + offset,
      rate: this.rate,
      loopStart: loop ? loop.start + offset : 0,
      loopEnd: loop ? loop.end + offset : 0,
    }));
    window.clearInterval(this.watch);
    this.watch = 0;
    if (playing && !loop) this.watch = window.setInterval(() => this.checkEnd(), 40);
    this.onChange();
  }

  private checkEnd(): void {
    if (!this.clock.playing || this.clock.loop || songAt(this.clock, this.ctx.currentTime) < this.duration) return;
    this.music.gain.setTargetAtTime(0, this.ctx.currentTime, 0.008);
    this.apply(this.duration, false);
    this.onEnded();
  }

  async play(): Promise<void> {
    if (!this.source || this.clock.playing) return;
    await this.ctx.resume();
    await this.loaded;
    if (this.clock.playing) return;
    this.music.gain.cancelScheduledValues(this.ctx.currentTime);
    this.music.gain.setValueAtTime(1, this.ctx.currentTime);
    this.apply(this.clock.song >= this.duration && !this.source.wrap ? 0 : this.clock.song, true);
  }

  pause(): void {
    if (!this.clock.playing) return;
    const song = this.nodeSong();
    // Ramp down so the stretcher's tail does not click; the new clock holds `song`.
    this.music.gain.setTargetAtTime(0, this.ctx.currentTime, 0.008);
    this.apply(song, false);
  }

  seek(t: number): void {
    if (!this.source) return;
    const d = this.duration;
    const song = this.source.wrap ? ((t % d) + d) % d : Math.min(Math.max(t, 0), d);
    this.apply(song, this.clock.playing);
  }

  /** Speed, 0.5 to 1; pitch is kept. */
  setRate(rate: number): void {
    this.rate = Math.min(1, Math.max(0.5, rate));
    this.apply(this.source ? this.nodeSong() : 0, this.clock.playing);
  }

  /** A practice loop in song seconds, or null for none (a looping source keeps looping its whole lap). */
  setLoop(loop: Loop | null): void {
    this.userLoop = loop;
    this.apply(this.source ? this.nodeSong() : 0, this.clock.playing);
  }
}
